'use client';

import React, { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AuthFormWrapper } from '@/app/(founder)/auth/_components/auth-form-wrapper';
import { DATA_ROOM_ROUTES, ROUTES } from '@/constants/routes';
import { AuthDto, AuthSchema } from '@/lib/api/schemas/auth.schema';
import { getAuthError } from '@/utils/get-auth-error';
import { useAppContext } from '@/context/app-context';
import { ErrorCode } from '@supabase/auth-js/src/lib/error-codes';
import { supabase } from '@/lib/api/utils/supabase-client';

type Props = {
    defaultCallback?: string;
};

export function LoginForm({ defaultCallback = ROUTES.Welcome }: Props) {
    const searchParams = useSearchParams();
    const callbackUrl = searchParams.get('callbackUrl') || defaultCallback;
    const email = searchParams.get('email') || '';
    const priceId = searchParams.get('priceId');
    const coupon = searchParams.get('coupon');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const { goToPage } = useAppContext();

    const isDataRoomAccessRequest = callbackUrl?.includes(
        DATA_ROOM_ROUTES.RequestAccess,
    );

    const [showPassword, setShowPassword] = useState(false);

    const form = useForm<AuthDto>({
        resolver: zodResolver(AuthSchema),
        defaultValues: {
            email,
            password: '',
        },
    });

    const onSubmit = async (values: AuthDto) => {
        setLoading(true);
        try {
            // TODO moved to fe
            // const { error } = await login(values);
            const { error } = await supabase.auth.signInWithPassword(values);

            if (error) {
                setError(
                    getAuthError(
                        (error.code as ErrorCode) ?? 'unexpected_failure',
                    ),
                );
            } else {
                // If priceId is present, redirect to checkout
                if (priceId) {
                    const checkoutParams = new URLSearchParams({ priceId });
                    if (coupon) {
                        checkoutParams.append('coupon', coupon);
                    }
                    goToPage(
                        `${ROUTES.CheckoutRedirect}?${checkoutParams.toString()}`,
                    );
                } else {
                    goToPage(callbackUrl);
                }
            }
        } catch (err: unknown) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const toggleShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const resetPassword = () => {
        goToPage(ROUTES.ForgotPassword);
    };

    const createAccount = () => {
        const params = new URLSearchParams({
            callbackUrl,
            email,
        });

        // Pass priceId and coupon to register form
        if (priceId) {
            params.append('priceId', priceId);
        }
        if (coupon) {
            params.append('coupon', coupon);
        }

        goToPage(`${ROUTES.Register}?${params.toString()}`);
    };

    return (
        <AuthFormWrapper
            footer={
                <div className="text-xs text-muted-foreground text-center">
                    Don't have an account?
                    <Button variant="link" size="xs" onClick={createAccount}>
                        Sign up
                    </Button>
                </div>
            }
        >
            <>
                {error && (
                    <Alert variant="destructive" className="mb-4">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-4"
                    >
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            type="email"
                                            placeholder="Enter your email address"
                                            disabled={loading}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="password"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <div className="relative">
                                            <Input
                                                {...field}
                                                type={
                                                    showPassword
                                                        ? 'text'
                                                        : 'password'
                                                }
                                                placeholder="Enter your password"
                                                disabled={loading}
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-0 top-0 h-full px-3"
                                                onClick={toggleShowPassword}
                                            >
                                                {showPassword ? (
                                                    <EyeOff size={16} />
                                                ) : (
                                                    <Eye size={16} />
                                                )}
                                            </Button>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className="flex items-center justify-between">
                            <Button
                                type="button"
                                variant="link"
                                size="xs"
                                onClick={resetPassword}
                                disabled={loading}
                            >
                                Forgot password?
                            </Button>
                        </div>
                        {form.formState.errors.root && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {form.formState.errors.root.message}
                                </AlertDescription>
                            </Alert>
                        )}
                        <Button
                            type="submit"
                            className="w-full"
                            loading={loading}
                            disabled={loading}
                        >
                            {isDataRoomAccessRequest
                                ? 'Access Data Room'
                                : 'Sign in'}
                        </Button>
                    </form>
                </Form>
            </>
        </AuthFormWrapper>
    );
}
